<template>
  <div class="spark-list">

    <!-- Not subscribed gate — shown when integration is inactive OR active but no paid tier -->
    <div class="spark-gate" v-if="integrationsChecked && (!isSparkActive || (tierLoaded && !tierSubscribed))">
      <div class="spark-gate__icon">
        <img src="/img/templates/spark.svg" width="72" height="72" alt="Spark" />
      </div>
      <h2 class="spark-gate__title">{{ $t('spark.gate.title') }}</h2>
      <p class="spark-gate__desc">{{ $t('spark.gate.desc') }}</p>
      <ul class="spark-gate__features">
        <li><i class="fa fa-check"></i> {{ $t('spark.gate.feature1') }}</li>
        <li><i class="fa fa-check"></i> {{ $t('spark.gate.feature2') }}</li>
        <li><i class="fa fa-check"></i> {{ $t('spark.gate.feature3') }}</li>
        <li><i class="fa fa-check"></i> {{ $t('spark.gate.feature4') }}</li>
      </ul>
      <button class="spark-gate__btn" @click="openMarketplace">
        <i class="icon-fire"></i>
        {{ $t('spark.gate.cta') }}
      </button>
    </div>

    <!-- Subscribed — normal list view (only render after tier is confirmed) -->
    <template v-else-if="tierLoaded && tierSubscribed">
      <!-- Header -->
      <div class="spark-header">
        <div class="spark-header__left">
          <h2 class="spark-header__title">Sparks</h2>
          <span class="spark-header__count" v-if="items">{{ items.length }}</span>
        </div>
        <div class="spark-header__right">
          <div class="spark-search">
            <i class="fa fa-search spark-search__icon"></i>
            <input
              type="text"
              class="spark-search__input"
              v-model="search"
              placeholder="Search sparks…"
            />
          </div>
          <button
            class="spark-btn spark-btn--create"
            @click="handleCreate"
            :disabled="!canCreate"
            :title="!canCreate ? $t('spark.limitReached') : ''"
          >
            <i class="fa fa-plus"></i>
            <span>New Spark</span>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="spark-grid" v-if="!items">
        <div class="spark-card spark-card--skeleton" v-for="n in 4" :key="n">
          <div class="spark-card__body">
            <div class="skel skel--title"></div>
            <div class="skel skel--line"></div>
            <div class="skel skel--line skel--short"></div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div class="spark-empty" v-else-if="items.length === 0">
        <div class="spark-empty__icon">
          <i class="fa fa-bolt"></i>
        </div>
        <p class="spark-empty__text">{{ $t('noSparks') }}</p>
        <button class="spark-btn spark-btn--create" @click="handleCreate">
          <i class="fa fa-plus"></i>
          <span>Create your first spark</span>
        </button>
      </div>

      <!-- Grid -->
      <div class="spark-grid" v-else>
      <div
        class="spark-card"
        v-for="item in filteredItems"
        :key="item.uuid"
        @click="$router.push(`/admin/spark/${item.uuid}`)"
      >
        <div class="spark-card__header">
          <div class="spark-card__icon">
            <i class="fa fa-bolt"></i>
          </div>
        </div>

        <div class="spark-card__body">
          <h3 class="spark-card__name">{{ item.name }}</h3>

          <div class="spark-card__versions">
            <div class="spark-card__version">
              <span class="spark-card__version-label">Dev</span>
              <span
                class="spark-card__version-value"
                :class="{ 'is-active': item.dev_version }"
              >{{ item.dev_version || 'inactive' }}</span>
            </div>
            <div class="spark-card__version">
              <span class="spark-card__version-label">Live</span>
              <span
                class="spark-card__version-value"
                :class="{ 'is-live': item.public_version }"
              >{{ item.public_version || 'unpublished' }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "SparkList",
  data() {
    return {
      items: null,
      search: "",
      integrationsChecked: false,
    };
  },
  computed: {
    isSparkActive() {
      return this.$store.getters.isIntegrationActive('protokol-spark')
    },
    tierLoaded() {
      return this.$store.getters['spark/tierLoaded']
    },
    tierSubscribed() {
      return this.$store.getters['spark/tierSubscribed']
    },
    filteredItems() {
      if (!this.items) return [];
      if (!this.search.trim()) return this.items;
      const q = this.search.toLowerCase().trim();
      return this.items.filter((i) =>
        i.name.toLowerCase().includes(q)
      );
    },
    canCreate() {
      return this.$store.getters['spark/canCreateSpark']
    },
  },
  methods: {
    openMarketplace() {
      this.$router.push({ name: 'marketplace.List', query: { open: 'protokol-spark' } })
    },
    handleCreate() {
      if (!this.canCreate) {
        this.openMarketplace()
        return
      }
      this.$router.push('/admin/spark/create')
    },
    loadSparks() {
      this.$store.dispatch('spark/loadTier').catch(() => {})
      this.$sandbox
        .spark()
        .all()
        .then((resp) => {
          this.items = resp.data;
        })
        .catch((err) => {
          this.$debugLog(err);
          this.$pageUnavailable();
        });
    },
  },
  watch: {
    isSparkActive(val) {
      if (val && this.items === null) {
        this.loadSparks()
      }
    },
  },
  mounted() {
    if (!this.$can("manage::spark sandbox")) {
      return this.$router.back();
    }

    this.$store.dispatch('fetchIntegrations').then(() => {
      this.integrationsChecked = true
      if (this.isSparkActive) {
        this.loadSparks()
      }
    }).catch(() => {
      this.integrationsChecked = true
    })
  },
};
</script>

<style scoped lang="scss">
// ─── Subscription gate ─────────────────────────
.spark-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 80px 24px 60px;
  max-width: 520px;
  margin: 0 auto;

  &__icon {
    margin-bottom: 28px;
    img {
      border-radius: 18px;
      box-shadow: 0 8px 32px rgba(245, 158, 11, 0.28);
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 12px;
  }

  &__desc {
    font-size: 15px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0 0 28px;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0 0 36px;
    text-align: left;
    width: 100%;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: var(--text-secondary);
      padding: 7px 0;
      border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.06));

      &:last-child { border-bottom: none; }

      i {
        color: #f59e0b;
        font-size: 12px;
        flex-shrink: 0;
      }
    }
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 32px;
    background: linear-gradient(135deg, #92400e 0%, #f59e0b 100%);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.35);
    transition: opacity 0.18s, transform 0.18s, box-shadow 0.18s;

    i { font-size: 16px; }

    &:hover {
      opacity: 0.9;
      transform: translateY(-2px);
      box-shadow: 0 6px 24px rgba(245, 158, 11, 0.45);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// ─── List ───────────────────────────────────────
.spark-list {
  padding: 8px 0;
}

.spark-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.spark-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.spark-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.spark-search {
  position: relative;
  display: flex;
  align-items: center;
}

.spark-search__icon {
  position: absolute;
  left: 12px;
  font-size: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.spark-search__input {
  width: 200px;
  height: 38px;
  padding: 0 14px 0 34px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

.spark-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.spark-header__count {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

// ─── Create button ─────────────────────────────
.spark-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.18s ease;

  &--create {
    padding: 9px 20px;
    background: var(--primary-start);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}

// ─── Grid ──────────────────────────────────────
.spark-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

// ─── Card ──────────────────────────────────────
.spark-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    border-color: rgba(108, 128, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  }
}

.spark-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 0;
}

.spark-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 193, 7, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffc107;
  font-size: 15px;
}

.spark-card__body {
  padding: 14px 18px 16px;
  flex: 1;
}

.spark-card__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 14px;
  line-height: 1.3;
  word-break: break-word;
}

.spark-card__versions {
  display: flex;
  gap: 16px;
}

.spark-card__version {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.spark-card__version-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-muted);
}

.spark-card__version-value {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: 'SFMono-Regular', 'Consolas', monospace;

  &.is-active {
    color: #57d9a3;
  }

  &.is-live {
    color: var(--primary-start);
  }
}

// ─── Empty state ───────────────────────────────
.spark-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.spark-empty__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(255, 193, 7, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-muted);
  margin-bottom: 16px;
}

.spark-empty__text {
  font-size: 15px;
  color: var(--text-muted);
  margin: 0 0 20px;
}

// ─── Skeleton ──────────────────────────────────
.spark-card--skeleton {
  pointer-events: none;
  padding: 18px;

  .skel {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    animation: skel-pulse 1.4s ease-in-out infinite;

    &--title {
      width: 60%;
      height: 18px;
      margin-bottom: 14px;
    }

    &--line {
      width: 80%;
      height: 12px;
      margin-bottom: 10px;
    }

    &--short {
      width: 45%;
    }
  }
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
</style>
